import { useState } from 'react';
import {
  Clock,
  Users,
  Calendar,
  TrendingUp,
  Play,
  UserPlus,
  Shield,
  Zap,
  Mail,
  Lock,
  User,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  MousePointer2,
} from 'lucide-react';
import { authService } from '../authService';

type AuthMode = 'signin' | 'signup';

function friendlyAuthError(message: string): string {
  if (message.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (message.includes('auth/invalid-email')) return 'Please enter a valid email address.';
  if (message.includes('auth/weak-password')) return 'Password should be at least 6 characters.';
  if (message.includes('auth/user-not-found') || message.includes('auth/wrong-password') || message.includes('auth/invalid-credential')) {
    return 'Incorrect email or password.';
  }
  if (message.includes('auth/too-many-requests')) return 'Too many attempts. Please try again later.';
  return message.replace(/^Firebase: Error \(auth\/[^)]+\)\.\s*/i, '') || 'Something went wrong. Please try again.';
}

const features = [
  {
    icon: Play,
    title: 'One-tap time tracking',
    description: 'Members start and stop a live timer. Every session saves automatically with clock-in and clock-out times.',
  },
  {
    icon: Shield,
    title: 'Privacy by role',
    description: 'Members only see their own hours. Managers get a full team overview—no one else\'s data leaks through.',
  },
  {
    icon: Calendar,
    title: 'Calendar with session details',
    description: 'Hover any day on the calendar to see exact clock-in and clock-out times for every session.',
  },
  {
    icon: Users,
    title: 'Multi-team workspaces',
    description: 'Create teams, join with invite codes, and switch between organizations from one account.',
  },
  {
    icon: TrendingUp,
    title: 'Daily, weekly & monthly views',
    description: 'Navigate periods with arrows, jump to today instantly, and review totals at a glance.',
  },
  {
    icon: Zap,
    title: 'Real-time sync',
    description: 'Firebase keeps timers and reports in sync across devices—refresh-safe and always up to date.',
  },
];

const roleCards = [
  {
    icon: Eye,
    title: 'Manager view',
    color: 'from-blue-500/20 to-purple-600/20 border-blue-500/30',
    items: [
      'See every team member\'s time',
      'Switch between members on the calendar',
      'Add, edit, and remove employees',
      'Share invite codes to grow the team',
    ],
  },
  {
    icon: EyeOff,
    title: 'Member view',
    color: 'from-emerald-500/20 to-teal-600/20 border-emerald-500/30',
    items: [
      'Large live timer—start and stop anytime',
      'Only your own stats and calendar',
      'Today, week, and month summaries',
      'Profile with your name and avatar',
    ],
  },
];

const AppPreview = () => (
  <div className="relative mx-auto max-w-lg">
    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-blue-500/20 to-purple-600/20 blur-2xl" />
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/90 shadow-2xl backdrop-blur-sm">
      <div className="flex items-center gap-2 border-b border-slate-700/50 bg-slate-800/80 px-4 py-3">
        <div className="h-3 w-3 rounded-full bg-red-500/80" />
        <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
        <div className="h-3 w-3 rounded-full bg-green-500/80" />
        <span className="ml-2 text-xs text-slate-400">Team Time Tracker</span>
      </div>
      <div className="space-y-4 p-5 text-left">
        {/* Header with avatar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-semibold text-white">
              UA
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Design Team</p>
              <p className="text-[10px] text-slate-400">Track your time</p>
            </div>
          </div>
          <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-xs text-green-300">Live</span>
        </div>

        {/* Timer */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 text-center">
          <span className="mb-2 inline-block rounded-full bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-0.5 text-xs text-white">
            You
          </span>
          <p className="font-mono text-3xl font-bold tracking-wider text-white">02:34:18</p>
          <button type="button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-red-500 to-rose-600 py-2 text-sm font-medium text-white">
            <Square className="h-4 w-4" />
            Stop timer
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          {['Today', 'Week', 'Month'].map((label, i) => (
            <div key={label} className="rounded-lg border border-slate-700/50 bg-slate-800/30 p-2 text-center">
              <p className="text-[10px] text-slate-400">{label}</p>
              <p className="text-sm font-bold text-white">{['4h 12m', '22h 05m', '86h 30m'][i]}</p>
            </div>
          ))}
        </div>

        {/* Mini calendar with tooltip */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-slate-400">Calendar</p>
            <div className="flex gap-1">
              {['Daily', 'Weekly', 'Monthly'].map((v, i) => (
                <span
                  key={v}
                  className={`rounded px-1.5 py-0.5 text-[9px] ${
                    i === 2 ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : 'text-slate-500'
                  }`}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[9px] text-slate-500">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <span key={`${d}-${i}`}>{d}</span>
            ))}
          </div>
          <div className="relative mt-1 grid grid-cols-7 gap-1">
            {Array.from({ length: 14 }, (_, i) => (
              <div
                key={i}
                className={`aspect-square rounded p-0.5 text-[9px] ${
                  i === 9 ? 'bg-slate-600/60 ring-1 ring-blue-500' : 'bg-slate-700/30 text-slate-500'
                }`}
              >
                {i + 1}
              </div>
            ))}
            {/* Mock hover tooltip */}
            <div className="absolute left-[28%] top-0 z-10 -translate-y-full rounded-lg border border-slate-600 bg-slate-900 px-2.5 py-2 shadow-xl">
              <p className="mb-1 text-[9px] font-semibold text-white">Mon, Jun 2</p>
              <p className="text-[8px] leading-relaxed">
                <span className="text-green-400">9:15 AM</span>
                <span className="text-slate-500"> → </span>
                <span className="text-red-400">11:30 AM</span>
                <span className="text-slate-400"> (2h 15m)</span>
              </p>
              <p className="text-[8px] leading-relaxed">
                <span className="text-green-400">1:00 PM</span>
                <span className="text-slate-500"> → </span>
                <span className="text-red-400">3:45 PM</span>
                <span className="text-slate-400"> (2h 45m)</span>
              </p>
              <div className="mt-1 flex items-center gap-1 border-t border-slate-700 pt-1">
                <MousePointer2 className="h-2.5 w-2.5 text-slate-500" />
                <span className="text-[8px] text-slate-500">Hover any day for details</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

function Square({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}

const LandingPage = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollToAuth = (mode: AuthMode = 'signup') => {
    setAuthMode(mode);
    setError('');
    document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const result = await authService.signInWithGoogle();
    if (!result.success) setError(friendlyAuthError(result.error || ''));
    setLoading(false);
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (authMode === 'signup') {
      if (!displayName.trim()) {
        setError('Please enter your name.');
        setLoading(false);
        return;
      }
      const result = await authService.signUp(email, password, displayName.trim());
      if (!result.success) setError(friendlyAuthError(result.error || ''));
    } else {
      const result = await authService.signIn(email, password);
      if (!result.success) setError(friendlyAuthError(result.error || ''));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-left">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-slate-900/80 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Team Time Tracker</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => scrollToAuth('signin')}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition hover:text-white sm:px-4"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => scrollToAuth('signup')}
              className="rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-lg transition hover:opacity-90 sm:px-4"
            >
              Get started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pb-16 pt-12 sm:px-6 sm:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-sm text-blue-300">
              <Zap className="h-4 w-4" />
              Professional team time tracking
            </p>
            <h1 className="mb-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Track team time.
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Everyone sees what they should.
              </span>
            </h1>
            <p className="mb-8 max-w-xl text-lg text-slate-300">
              Members clock in with a live timer and review only their own hours. Managers see the
              full team—calendar, reports, and clock-in/out details on every day.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => scrollToAuth('signup')}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg transition hover:opacity-90"
              >
                Create free account
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollToAuth('signin')}
                className="rounded-xl border border-slate-600 bg-slate-800/50 px-6 py-3 font-semibold text-white transition hover:bg-slate-800"
              >
                I have an account
              </button>
            </div>
            <ul className="mt-8 space-y-2">
              {[
                'Members only see their own time',
                'Hover calendar days for clock-in/out times',
                'Google or email sign-up — no credit card',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-slate-400">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-green-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <AppPreview />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-white/5 bg-slate-900/50 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white sm:text-4xl">Everything you need</h2>
            <p className="mx-auto max-w-2xl text-slate-400">
              Role-based dashboards, session-level calendar details, and real-time sync—built for how teams actually work.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6 transition hover:border-purple-500/30 hover:bg-slate-800/50"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-600/20">
                  <Icon className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="mb-2 font-semibold text-white">{title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold text-white sm:text-4xl">Two views, one app</h2>
            <p className="mx-auto max-w-2xl text-slate-400">
              Team creators become managers automatically. Everyone who joins with an invite code tracks as a member.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {roleCards.map(({ icon: Icon, title, color, items }) => (
              <div
                key={title}
                className={`rounded-2xl border bg-gradient-to-br p-6 ${color}`}
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900/40">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{title}</h3>
                </div>
                <ul className="space-y-2.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-12 text-center text-3xl font-bold text-white">How it works</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              { step: '1', title: 'Create your account', desc: 'Sign up with Google or email. Your profile and avatar are ready instantly.' },
              { step: '2', title: 'Create or join a team', desc: 'Managers create a workspace and share an invite code. Members join in one step.' },
              { step: '3', title: 'Track and review', desc: 'Run your timer, browse daily/weekly/monthly totals, and hover the calendar for session times.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-lg font-bold text-white">
                  {step}
                </div>
                <h3 className="mb-2 font-semibold text-white">{title}</h3>
                <p className="text-sm text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auth */}
      <section id="auth-section" className="border-t border-white/5 py-16 sm:py-20">
        <div className="mx-auto max-w-md px-4 sm:px-6">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-lg">
            <div className="border-b border-white/10 p-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
                <UserPlus className="h-7 w-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                {authMode === 'signup' ? 'Create your account' : 'Welcome back'}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {authMode === 'signup'
                  ? 'Start tracking time with your team today'
                  : 'Sign in to continue to your teams'}
              </p>
            </div>

            <div className="flex border-b border-white/10">
              <button
                type="button"
                onClick={() => { setAuthMode('signup'); setError(''); }}
                className={`flex-1 py-3 text-sm font-medium transition ${
                  authMode === 'signup' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Sign up
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode('signin'); setError(''); }}
                className={`flex-1 py-3 text-sm font-medium transition ${
                  authMode === 'signin' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Sign in
              </button>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="mb-4 flex w-full items-center justify-center gap-3 rounded-lg bg-white py-3 font-semibold text-gray-900 transition hover:bg-gray-100 disabled:opacity-60"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-900/80 px-2 text-slate-500">or with email</span>
                </div>
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-4">
                {authMode === 'signup' && (
                  <div>
                    <label htmlFor="displayName" className="mb-1.5 block text-xs font-medium text-slate-400">
                      Full name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Your name"
                        className="w-full rounded-lg border border-slate-600 bg-slate-800/50 py-2.5 pl-10 pr-4 text-white placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        autoComplete="name"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-400">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full rounded-lg border border-slate-600 bg-slate-800/50 py-2.5 pl-10 pr-4 text-white placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-slate-400">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full rounded-lg border border-slate-600 bg-slate-800/50 py-2.5 pl-10 pr-4 text-white placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
                >
                  {loading ? 'Please wait…' : authMode === 'signup' ? 'Create account' : 'Sign in'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <p>© {new Date().getFullYear()} Team Time Tracker. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
